import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import ProfileThunk from "../../services/thunks/ProfileThunk";
import { UserLocation, UserPreferences, UserProfile } from "../../interfaces/UserObject";
import { resetAllState } from "./resetAllState";
import { AppConstants } from "../../constants/AppConstants";

const profileSlice = createSlice({
  name: "ProfileDb",
  initialState: {
    data: null as UserProfile | null,
    userPreferences: AppConstants.DEFAULT_USER_PREFERENCES as UserPreferences,
    loadingMap: {} as any,
    errorMap: {} as any,
    successMap: {} as any,
  },
  reducers: {
    setProfile(state, action: PayloadAction<UserProfile>) {
      state.data = action.payload;
    },
    setUserPreferences(state, action: PayloadAction<UserPreferences>) {
      state.userPreferences = action.payload;
    },
    setUserLocationInPreferences(state, action: PayloadAction<UserLocation>) {
      const userLocation = action.payload;
      state.userPreferences.userLocation = userLocation;
    },
    updateLocalFindsPreferences(
      state,
      action: PayloadAction<{
        sortPreference: "NEAREST" | "RECENT" | string;
        ninefindsRadius: number;
      }>
    ) {
      // Update userPreferences with localFinds settings
      state.userPreferences = {
        ...state.userPreferences,
        searchSortBy: action.payload.sortPreference,
        ninefindsRadius: action.payload.ninefindsRadius,
      };
      // Future: This is where backend sync would be triggered
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(ProfileThunk.profileUpdate.pending, (state) => {
        state.loadingMap.profileUpdate = "Updating profile...";
        state.successMap.profileUpdate = null;
        state.errorMap.profileUpdate = null;
      })
      .addCase(ProfileThunk.profileUpdate.fulfilled, (state, action) => {
        state.loadingMap.profileUpdate = null;
        state.successMap.profileUpdate = "Profile updated successfully!";
        state.data = action.payload;

        if (action.payload?.userPreferencesDto) {
          const { userLatitude, userLongitude, userLocation: userLocationDisplayName, ...rest } = action.payload.userPreferencesDto;
          state.userPreferences = {
            ...rest,
            userLocation: {
              userLatitude,
              userLongitude,
              userLocationDisplayName
            }
          };
        }
      })
      .addCase(ProfileThunk.profileUpdate.rejected, (state, action) => {
        state.loadingMap.profileUpdate = null;
        state.errorMap.profileUpdate = "Error updating profile";
        state.successMap.profileUpdate = null;
      })
      .addCase(ProfileThunk.fetchProfile.pending, (state) => {
        state.loadingMap.fetchProfile = true;
        state.errorMap.fetchProfile = null;
      })
      .addCase(ProfileThunk.fetchProfile.fulfilled, (state, action) => {
        state.loadingMap.fetchProfile = false;
        state.data = action.payload;
        if (state.data.userPreferencesDto == null) {
          state.data.userPreferencesDto = "{}"
        }
        const { userLatitude, userLongitude, userLocation: userLocationDisplayName, ...rest } = action.payload.userPreferencesDto;
        state.userPreferences = {
          ...rest,
          userLocation: {
            userLatitude,
            userLongitude,
            userLocationDisplayName
          }
        };
      })
      .addCase(ProfileThunk.fetchProfile.rejected, (state, action) => {
        state.loadingMap.fetchProfile = false;
        state.errorMap.fetchProfile = action.payload;
      })
      .addCase(ProfileThunk.deleteAccount.pending, (state) => {
        state.loadingMap.deleteAccount = "Deleting account...";
        state.successMap.deleteAccount = null;
        state.errorMap.deleteAccount = null;
      })
      .addCase(ProfileThunk.deleteAccount.fulfilled, (state) => {
        // No data to store — the caller logs the user out, which triggers
        // resetAllState and clears this slice entirely.
        state.loadingMap.deleteAccount = null;
        state.successMap.deleteAccount = "Account deleted successfully.";
        state.errorMap.deleteAccount = null;
      })
      .addCase(ProfileThunk.deleteAccount.rejected, (state) => {
        state.loadingMap.deleteAccount = null;
        state.successMap.deleteAccount = null;
        state.errorMap.deleteAccount = "Error deleting account";
      })
      .addCase(ProfileThunk.validateEmail.pending, (state) => {
        state.loadingMap.validateEmail = true;
        state.errorMap.validateEmail = null;
      })
      .addCase(ProfileThunk.validateEmail.fulfilled, (state, action) => {
        state.loadingMap.validateEmail = false;
        state.successMap.validateEmail = "OTP sent successfully!";
      })
      .addCase(ProfileThunk.validateEmail.rejected, (state, action) => {
        state.loadingMap.validateEmail = false;
        state.errorMap.validateEmail = action.error.message;
      })
      .addCase(ProfileThunk.updateUserPreferences.pending, (state) => {
        // fill this
      })
      .addCase(ProfileThunk.updateUserPreferences.fulfilled, (state, action) => {
        const { userLatitude, userLongitude, userLocation: userLocationDisplayName, ...rest } = action.payload;
        state.userPreferences = {
          ...rest,
          userLocation: {
            userLatitude,
            userLongitude,
            userLocationDisplayName
          }
        };
      })
      .addCase(ProfileThunk.updateUserPreferences.rejected, (state, action) => {
        // fill this
      })
      .addCase(resetAllState, () => ({
        data: null,
        userPreferences: AppConstants.DEFAULT_USER_PREFERENCES as UserPreferences,
        loadingMap: {},
        errorMap: {},
        successMap: {},
      }));
  },
});

export const { setProfile, setUserPreferences, updateLocalFindsPreferences, setUserLocationInPreferences } =
  profileSlice.actions;
export default profileSlice.reducer;

// Selector to get current user's phone number
export const getUserPhone = (state: { profile: { data: { phone: string } | null } }): string | null =>
  state.profile.data?.phone ?? null;
